<script>
	import { onMount } from "svelte";
	import { deviceDistributionAll as all, menu, menuSub, menuSubSub } from "../../stores";
	import { postApi as post, putApi as put, delApi as del } from "../../service/api";
	import { Dates } from "../../utils/date";
	import TableToolbar from "../../components/TableToolbar.svelte";
	import Pagination from "../../components/Pagination.svelte";
	import Tab from "../../components/Tab.svelte";

	// 상수 정의
	const BASE_URL = "/device/distribution";
	const PAGE_SIZES = [10, 15, 20, 25, 30, 50, 100];
	const LOCALE_INFO = {
		kr: { value: "KR", text: "한국" },
		us: { value: "US", text: "미국" },
	};
	const ACTIVE_OPTIONS = [
		{ value: true, text: "사용" },
		{ value: false, text: "미사용" },
	];

	// 상태 관리
	let isEditMode = false;
	let editingRowId = null;
	let focusField = "name";
	let activeTab = "all";
	let filteredData = [];
	let currentPage = 1;
	let totalPages = 1;
	let totalItems = 0;
	let pageSize = 10;

	// 편집 아이템 초기값
	const defaultEditItem = {
		ID: 0,
		name: "",
		localeInfo: "kr",
		isActive: true,
	};

	let editItem = { ...defaultEditItem };

	const tabs = [{ key: "all", text: "전체 유통채널" }, ...Object.entries(LOCALE_INFO).map(([key, info]) => ({ key, text: info.text }))];

	// 메서드
	const setFocus = (field = "name") => {
		focusField = field;
	};

	const validateItem = () => {
		if (!editItem.name) {
			alert("유통채널명을 입력하세요.");
			setFocus("name");
			return false;
		}
		return true;
	};

	const handleSave = async () => {
		if (!validateItem()) return;

		const payload = {
			...editItem,
			isActive: Boolean(editItem.isActive),
		};

		try {
			if (editingRowId) {
				console.log("PUT 요청:", payload);
				await put(BASE_URL, payload);
			} else {
				console.log("POST 요청:", payload);
				await post(BASE_URL, payload);
			}
			await fnSearch();
		} catch (error) {
			console.error("저장 중 오류 발생:", error);
			alert("저장 중 오류가 발생했습니다.");
		}
	};

	const fnSearch = async () => {
		const url = activeTab === "all" ? `${BASE_URL}/all/${pageSize}/${currentPage}` : `${BASE_URL}/${activeTab}/${pageSize}/${currentPage}`;
		await all.fetch(url);
		resetEdit();
	};

	const resetEdit = () => {
		isEditMode = false;
		editingRowId = null;
		editItem = { ...defaultEditItem };
	};

	const fnProcessRegist = () => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = null;
			editItem = {
				...defaultEditItem,
				localeInfo: activeTab === "all" ? "kr" : activeTab,
			};
			focusFirstInput();
		}
	};

	const focusFirstInput = () => {
		setTimeout(() => {
			const nameInput = document.querySelector('input[name="name"]');
			if (nameInput) {
				nameInput.focus();
				nameInput.select();
			}
		});
	};

	const fnProcessEdit = (item) => {
		if (!isEditMode) {
			isEditMode = true;
			editingRowId = item.ID;
			editItem = {
				ID: item.ID,
				name: item.name,
				localeInfo: item.localeInfo,
				isActive: item.isActive,
			};
			focusFirstInput();
		}
	};

	const fnDelete = async (id) => {
		const item = $all.data.list.find((item) => item.ID === id);
		if (item.isActive) {
			alert("사용 중인 유통채널은 삭제할 수 없습니다.");
			return;
		}

		if (confirm("정말 삭제하시겠습니까?")) {
			await del(BASE_URL, { ID: id });
			await fnSearch();
		}
	};

	const fnRegistServer = () => {
		if (confirm("서버에 적용하시겠습니까?")) {
			put(`${BASE_URL}/cache`, {}).then((success) => {
				if (success) {
					alert("서버 적용이 완료되었습니다.");
				}
			});
		}
	};

	// 탭 변경 핸들러
	const handleTabClick = (tab) => {
		activeTab = tab;
		fnSearch();
	};

	const handlePageSizeChange = (event) => {
		pageSize = parseInt(event.target.value); // pageSize 값 업데이트
		currentPage = 1;
		fnSearch();
	};

	const handlePageChange = (page) => {
		if (page < 1 || page > totalPages) return;
		currentPage = page;
		fnSearch();
	};

	$: {
		const data = $all?.data || {};
		filteredData = data.list || [];
		totalPages = Math.ceil((data.total || 0) / pageSize);
		totalItems = data.total || 0;
	}

	onMount(() => {
		menu.set("디바이스 관리");
		menuSub.set("유통채널 관리");
		menuSubSub.set("");
		fnSearch();
	});
</script>

<div class="card mb-4">
	<div class="table-responsive text-nowrap">
		<Tab {tabs} {activeTab} onTabChange={handleTabClick} />
		<div class="ms-auto" style="padding: 5px;">
			<TableToolbar
				{totalItems}
				{currentPage}
				{totalPages}
				{pageSize}
				pageSizes={PAGE_SIZES}
				onPageSizeChange={handlePageSizeChange}
				onReload={fnSearch}
				onRegist={fnProcessRegist}
				onRegistServer={fnRegistServer}
			/>
		</div>
		<div class="fade-container {activeTab ? 'show' : ''}">
			<table class="table small">
				<thead>
					<tr style="text-align:center">
						<th width="80">No</th>
						<th width="*">유통채널명</th>
						<th width="120">지역 정보</th>
						<th width="120">사용 여부</th>
						<th width="150">등록일시</th>
						<th width="150">수정일시</th>
						<th width="150">관리</th>
					</tr>
				</thead>
				<tbody>
					{#if isEditMode && editingRowId === null}
						<tr style="text-align:center">
							<td />
							<td>
								<input
									type="text"
									name="name"
									class="form-control form-control-sm text-center"
									bind:value={editItem.name}
									on:focus={() => setFocus("name")}
								/>
							</td>
							<td>
								<select class="form-select form-select-sm text-center" bind:value={editItem.localeInfo}>
									{#each Object.values(LOCALE_INFO) as { value, text }}
										<option {value}>{text}</option>
									{/each}
								</select>
							</td>
							<td>
								<div class="form-check form-switch d-flex justify-content-center">
									<input class="form-check-input" type="checkbox" id="isActive-new" bind:checked={editItem.isActive} />
								</div>
							</td>
							<td />
							<td />
							<td>
								<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
								<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
							</td>
						</tr>
					{/if}

					{#if filteredData.length === 0}
						<tr>
							<td colspan="7" class="text-center py-4" style="font-size: 12px;">데이터가 없습니다</td>
						</tr>
					{:else}
						{#each filteredData as item, index}
							{#if editingRowId === item.ID}
								<tr style="text-align:center">
									<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
									<td>
										<input
											type="text"
											name="name"
											class="form-control form-control-sm text-center"
											bind:value={editItem.name}
											on:focus={() => setFocus("name")}
										/>
									</td>
									<td>
										<select class="form-select form-select-sm text-center" bind:value={editItem.localeInfo}>
											{#each Object.values(LOCALE_INFO) as { value, text }}
												<option {value}>{text}</option>
											{/each}
										</select>
									</td>
									<td>
										<div class="form-check form-switch d-flex justify-content-center">
											<input class="form-check-input" type="checkbox" id="isActive-edit" bind:checked={editItem.isActive} />
										</div>
									</td>
									<td>{Dates.defaultConvertFull(item.CreatedAt)}</td>
									<td>{Dates.defaultConvertFull(item.UpdatedAt)}</td>
									<td>
										<button class="btn btn-sm btn-primary me-1" on:click={handleSave}>Save</button>
										<button class="btn btn-sm btn-outline-secondary" on:click={resetEdit}>Cancel</button>
									</td>
								</tr>
							{:else}
								<tr style="text-align:center; font-size: 12px;">
									<td>{totalItems - ((currentPage - 1) * pageSize + index)}</td>
									<td>{item.name}</td>
									<td><b>{LOCALE_INFO[item.localeInfo]?.text || item.localeInfo}</b></td>
									<td>
										<div class="form-check form-switch d-flex justify-content-center">
											<input class="form-check-input" type="checkbox" checked={item.isActive} disabled readonly />
										</div>
									</td>
									<td>{Dates.defaultConvertFull(item.CreatedAt)}</td>
									<td>{Dates.defaultConvertFull(item.UpdatedAt)}</td>
									<td>
										<button class="btn btn-sm btn-outline-primary me-1" on:click={() => fnProcessEdit(item)}>Edit</button>
										<button class="btn btn-sm btn-outline-primary" on:click={() => fnDelete(item.ID)}>Delete</button>
									</td>
								</tr>
							{/if}
						{/each}
					{/if}
				</tbody>
			</table>
		</div>
		<Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
	</div>
</div>

<style>
	.fade-container {
		opacity: 0;
		transition: opacity 0.15s linear;
	}

	.fade-container.show {
		opacity: 1;
	}
</style>
